import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import * as moment from 'moment';
import { Moment } from 'moment';
import { finalize, Subject, takeUntil } from 'rxjs';
import {
  DATE_FORMAT,
  DATE_MODE_FORMATS,
  isFlagOn,
  ReportDownloadDialogData,
  YEAR_FORMAT,
  YEAR_MODE_FORMATS,
} from '../models/report';
import { ReportService } from '../services/report.service';

@Component({
  selector: 'app-report-download-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatDatepickerModule,
    MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule],
  providers: [
    { provide: DateAdapter, useClass: MomentDateAdapter },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { strict: true } },
    {
      provide: MAT_DATE_FORMATS,
      deps: [MAT_DIALOG_DATA],
      useFactory: (data: ReportDownloadDialogData) =>
        data.report.dateRangeType === 'year' ? YEAR_MODE_FORMATS : DATE_MODE_FORMATS,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './report-download-dialog.component.html',
  styleUrls: ['./report-download-dialog.component.scss'],
})
export class ReportDownloadDialogComponent implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly showStartDate: boolean;
  readonly showEndDate: boolean;
  readonly isYearOnly: boolean;
  readonly format: 'csv' | 'pdf';
  readonly startLabel: string;
  readonly endLabel: string;
  readonly minDate: Moment;
  readonly maxDate: Moment;
  readonly form: FormGroup;
  isLoading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly data: ReportDownloadDialogData,
    private readonly dialogRef: MatDialogRef<ReportDownloadDialogComponent, boolean>,
    private readonly fb: FormBuilder,
    private readonly reportService: ReportService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {
    const report = data.report;
    this.showStartDate = isFlagOn(report.startDt);
    this.showEndDate = isFlagOn(report.endDt);
    this.isYearOnly = report.dateRangeType === 'year';
    this.format = report.downloadFormat || 'csv';
    this.minDate = moment(this.isYearOnly ? data.minYearDate : data.minDate);
    this.maxDate = moment(this.isYearOnly ? data.maxYearDate : data.maxDate);

    const both = this.showStartDate && this.showEndDate;
    this.startLabel = this.isYearOnly ? (both ? 'From year' : 'Year') : 'Start date';
    this.endLabel = this.isYearOnly ? (both ? 'To year' : 'Year') : 'End date';

    this.form = this.fb.group(
      {
        start: [null, this.showStartDate ? Validators.required : []],
        end: [null, this.showEndDate ? Validators.required : []],
      },
      { validators: (group: AbstractControl) => this.dateRangeValidator(group) },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasInputs(): boolean {
    return this.showStartDate || this.showEndDate;
  }

  startDateFilter = (date: Moment | null): boolean => {
    const end = this.form.get('end')?.value;
    return !!date && (!end || moment(date).isSameOrBefore(moment(end)));
  };

  endDateFilter = (date: Moment | null): boolean => {
    const start = this.form.get('start')?.value;
    return !!date && (!start || moment(date).isSameOrAfter(moment(start)));
  };

  chosenYearHandler(normalizedYear: Moment, datepicker: MatDatepicker<Moment>, controlName: 'start' | 'end'): void {
    const control = this.form.get(controlName);
    const selected = control?.value ? moment(control.value) : moment();
    selected.year(normalizedYear.year());
    control?.setValue(selected);
    datepicker.close();
  }

  download(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { start, end } = this.form.getRawValue();
    const dateFormat = this.isYearOnly ? YEAR_FORMAT : DATE_FORMAT;
    const startDate = this.showStartDate && start ? moment(start).format(dateFormat) : null;
    const endDate = this.showEndDate && end ? moment(end).format(dateFormat) : null;
    const fileName = this.data.report.name.replace(/\s+/g, '-').toLocaleLowerCase();

    this.isLoading = true;
    this.form.disable();
    this.reportService
      .downloadFile(fileName, this.data.report.endpoint, startDate, endDate, this.format)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.form.enable();
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.snackBar.open(`${this.format.toUpperCase()} file downloaded successfully.`, 'Close', {
            duration: 3000,
            panelClass: ['success-snackbar'],
          });
          this.dialogRef.close(true);
        },
        error: (err) =>
          this.snackBar.open(
            err?.message || `Failed to download ${this.format.toUpperCase()}. Please try again later.`,
            'Close',
            { duration: 5000, panelClass: ['error-snackbar'] },
          ),
      });
  }

  cancel(): void {
    if (!this.isLoading) this.dialogRef.close(false);
  }

  private dateRangeValidator(group: AbstractControl): ValidationErrors | null {
    const start = group.get('start')?.value;
    const end = group.get('end')?.value;
    if (!this.showStartDate || !this.showEndDate || !start || !end) return null;
    return moment(start).isAfter(moment(end)) ? { dateRangeInvalid: true } : null;
  }
}
